import axios from 'axios';

// Get API base URL
const getApiBaseUrl = () => {
  // Check if explicitly set via environment variable
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL;
    // If relative path, prepend origin
    if (apiUrl.startsWith('/')) {
      return window.location.origin + apiUrl;
    }
    return apiUrl;
  }
  
  // If running on production domain, use relative path
  if (window.location.hostname && window.location.hostname !== 'localhost') {
    return window.location.origin + '/aaimsdm/api';
  }
  
  // For development on localhost, use localhost:5000
  return 'http://localhost:5000/api';
};

// Create axios instance
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Bearer token if user is logged in
apiClient.interceptors.request.use(
  (config) => {
    // Try to get user from localStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        // Add a Bearer token to authenticate the request
        // In production, this should be a proper JWT token
        config.headers.Authorization = `Bearer ${btoa(userStr).substring(0, 50)}`;
      }
    } catch (error) {
      // Error reading user from localStorage - continue without token
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle session expiration (401) - redirect to login
    if (error.response?.status === 401) {
      // Clear user data from localStorage
      localStorage.removeItem('user');
      sessionStorage.removeItem('clientLogo');
      sessionStorage.removeItem('companyName');
      
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/aaimsdm/login' && window.location.pathname !== '/login') {
        // Use window.location for a full page reload to clear all state
        window.location.href = '/aaimsdm/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => apiClient.post('/auth/login', { username, password }),
  logout: () => apiClient.post('/auth/logout'),
  checkSession: () => apiClient.get('/auth/check-session'),
};

// Menu API
export const menuAPI = {
  getMenuHierarchy: () => apiClient.get('/menus/items/hierarchy'),
  getMenuItemTableName: (menuItemId) => apiClient.get(`/menus/items/${menuItemId}/table-name`),
};

// Menu Management API
export const menuManagementAPI = {
  getMenuItems: () => apiClient.get('/menus/items/all'),
  getParentFolders: () => apiClient.get('/menus/items/all'),
  createMenuItem: (data) => apiClient.post('/menus/items', data),
  updateMenuItem: (menuItemId, data) => apiClient.put(`/menus/items/${menuItemId}`, data),
  deleteMenuItem: (menuItemId) => apiClient.delete(`/menus/items/${menuItemId}`),
};

// Record API
export const recordAPI = {
  getRecords: (tableName, page, pageSize, search, searchField, sortBy, sortOrder, keywordSearch, filters) => {
    const params = { page, pageSize, sortBy, sortOrder };
    // Support both old and new search methods
    if (keywordSearch || filters) {
      params.keywordSearch = keywordSearch || null;
      if (filters) {
        params.filters = JSON.stringify(filters);
      }
    } else {
      params.search = search || null;
      params.searchField = searchField || null;
    }
    return apiClient.get(`/records/${tableName}`, { params });
  },
  getRecord: (tableName, recordId) => apiClient.get(`/records/${tableName}/${recordId}`),
  getRecordCount: (tableName) => apiClient.get(`/tables/${tableName}/count`),
  createRecord: (tableName, data) => apiClient.post(`/records/${tableName}`, data),
  updateRecord: (tableName, recordId, data) => apiClient.put(`/records/${tableName}/${recordId}`, data),
  deleteRecord: (tableName, recordId) => apiClient.delete(`/records/${tableName}/${recordId}`),
  getAllXbyXConfigs: () => apiClient.get(`/records/cross-table/all-configs`),
  getCrossTableConfig: (xbyxID) => apiClient.get(`/records/cross-table/config/${xbyxID}`),
  getCrossTableAvailableItems: (xbyxID, tableName) => apiClient.get(`/records/cross-table/available/${xbyxID}`, { params: { tableName } }),
  getCrossTableSelectedItems: (xbyxID, recordID, tableName) => apiClient.get(`/records/cross-table/selected/${xbyxID}/${recordID}`, { params: { currentTable: tableName } }),
  saveCrossTableRelationships: (xbyxID, recordID, selectedItems, tableName) => apiClient.post(`/records/cross-table/save/${xbyxID}/${recordID}`, { selectedItems, tableName }),
};

// MGB Setup API
export const mgbSetupAPI = {
  getTables: () => apiClient.get('/mgb-setup/tables'),
  getAvailableTables: () => apiClient.get('/mgb-setup/available-tables'),
  getAvailableViews: () => apiClient.get('/mgb-setup/available-views'),
  getFields: (tableId) => apiClient.get(`/mgb-setup/tables/${tableId}/fields`),
  getTableFields: (tableName) => apiClient.get(`/mgb-setup/table-fields-info/${tableName}`),
  getTableFieldsInfo: (tableName) => apiClient.get(`/mgb-setup/table-fields-info/${tableName}`),
  getTableFieldsBasic: (tableName) => apiClient.get(`/mgb-setup/table-fields/${tableName}`),
  getTableFieldsList: (tableID) => apiClient.get(`/mgb-setup/tables/${tableID}/fields`),
  registerTable: (data) => apiClient.post('/mgb-setup/register-table', data),
  updateTable: (tableID, data) => apiClient.put(`/mgb-setup/tables/${tableID}`, data),
  deleteTable: (tableID) => apiClient.delete(`/mgb-setup/tables/${tableID}`),
  updateFields: (tableID, fieldsToUpdate) => apiClient.post(`/mgb-setup/tables/${tableID}/update-fields`, { fields: fieldsToUpdate }),
  updateField: (fieldID, data) => apiClient.put(`/mgb-setup/fields/${fieldID}`, data),
  getFormats: (formatType) => apiClient.get('/mgb-setup/formats', { params: formatType ? { formatType } : {} }),
  getTableByName: (tableName) => apiClient.get(`/mgb-setup/table-by-name/${tableName}`),
};

// Permissions API
export const permissionsAPI = {
  getGroupPermissions: (groupId) => apiClient.get(`/permissions/group/${groupId}`),
};

// Table API
export const tableAPI = {
  getAllTables: () => apiClient.get('/tables'),
  getTableStructure: (tableName) => apiClient.get(`/tables/${tableName}/structure`),
  getTableFields: (tableName) => apiClient.get(`/tables/${tableName}/fields`),
  getPrimaryKey: (tableName) => apiClient.get(`/tables/${tableName}/primary-key`),
  getForeignKeys: (tableName) => apiClient.get(`/tables/${tableName}/foreign-keys`),
  getMGBTableMetadata: (tableName) => apiClient.get(`/mgb-setup/table-by-name/${tableName}`),
  getAllForeignKeys: () => apiClient.get('/tables/all/foreign-keys'),
  getFieldLinks: (tableName) => apiClient.get(`/tables/${tableName}/field-links`),
  getForeignKeyData: (tableName, referencedTable, idColumn, displayColumns) => 
    apiClient.get(`/tables/${tableName}/foreign-key-data/${referencedTable}/${idColumn}/${displayColumns}`),
  getLinkedFieldOptions: (tableName, linkedTableName, linkedFieldName) =>
    apiClient.get(`/tables/${tableName}/linked-field-options/${linkedTableName}/${linkedFieldName}`),
  checkDuplicate: (tableName, data, excludeId) =>
    apiClient.post(`/tables/${tableName}/check-duplicate`, { data, excludeId }),
};

// User Settings API
export const userSettingsAPI = {
  getProfile: () => apiClient.get('/user-settings/profile'),
  updateProfile: (data) => apiClient.put('/user-settings/profile', data),
  updatePassword: (data) => apiClient.put('/user-settings/password', data),
  updateProfilePhoto: (dataUrl) => apiClient.post('/user-settings/profile-photo', { photoUrl: dataUrl }),
  getMenuLocation: () => apiClient.get('/user-settings/menu-location'),
};

// Client API
export const clientAPI = {
  getClientInfo: (clientId) => apiClient.get(`/clients/${clientId}`),
};

// Admin Client API
export const adminClientAPI = {
  getAllClients: () => apiClient.get('/admin/clients'),
  createClient: (data) => apiClient.post('/admin/clients', data),
  updateClient: (clientId, data) => apiClient.put(`/admin/clients/${clientId}`, data),
  deleteClient: (clientId) => apiClient.delete(`/admin/clients/${clientId}`),
};

export default apiClient;

